import React, { Component } from 'react';
import { AlertTriangle, RotateCcw, Bug } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { error as logError } from '../../utils/logger';
import { isStrictMode } from '../../utils/strictMode';
interface QuestionErrorBoundaryProps {
  children: ReactNode;
  questionId: string;
  onReset: () => void;
}
interface QuestionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}
/**
 * QuestionErrorBoundary - Error boundary for question renderers
 *
 * Prevents renderer crashes from breaking the entire practice session.
 * Shows a friendly fallback UI with reset functionality.
 *
 * Features:
 * - Catches rendering errors in question components
 * - Logs errors with question context for debugging
 * - Provides user-friendly fallback UI
 * - Allows session continuation via reset
 * - Shows technical details in dev mode
 *
 * Usage:
 * ```tsx
 * <QuestionErrorBoundary questionId={question.id} onReset={handleReset}>
 *   <QuestionRenderer {...props} />
 * </QuestionErrorBoundary>
 * ```
 */
export class QuestionErrorBoundary extends Component<QuestionErrorBoundaryProps, QuestionErrorBoundaryState> {
  constructor(props: QuestionErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }
  static getDerivedStateFromError(error: Error): QuestionErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error with question context
    logError('Question Renderer Error:', {
      questionId: this.props.questionId,
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
    this.setState({
      errorInfo
    });
  }
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    this.props.onReset();
  };
  render(): ReactNode {
    // Strict mode: bypass error boundary entirely — let errors propagate for debugging
    if (isStrictMode()) {
      return this.props.children;
    }
    if (this.state.hasError) {
      return <Card className="p-8 sm:p-12 text-center border-2 border-rose-100 bg-rose-50/30" data-id="element-4620">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-6 border-4 border-rose-100" data-id="element-4621">
            <AlertTriangle className="w-8 h-8 text-rose-600" data-id="element-4622" />
          </div>

          <h3 className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-4623">
            Deze vraag kon niet worden geladen
          </h3>

          <p className="text-slate-600 mb-6 text-lg leading-relaxed font-medium max-w-md mx-auto" data-id="element-4624">
            Er ging iets mis bij het laden van deze vraag. Je kunt doorgaan met
            de volgende vraag.
          </p>

          {/* Dev mode: Show error details */}
          {process.env.NODE_ENV === 'development' && this.state.error && <div className="mb-6 p-4 bg-white rounded-xl border-2 border-rose-200 text-left max-w-2xl mx-auto" data-id="element-4625">
              <div className="flex items-start gap-3 mb-3" data-id="element-4626">
                <Bug className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" data-id="element-4627" />
                <div className="flex-1 min-w-0" data-id="element-4628">
                  <p className="text-xs font-bold text-rose-800 mb-2" data-id="element-4629">
                    Debug Info (Development Mode)
                  </p>
                  <p className="text-xs font-mono text-rose-700 break-all mb-2" data-id="element-4630">
                    <strong data-id="element-4631">Question ID:</strong> {this.props.questionId}
                  </p>
                  <p className="text-xs font-mono text-rose-700 break-all mb-2" data-id="element-4632">
                    <strong data-id="element-4633">Error:</strong> {this.state.error.message}
                  </p>
                  {this.state.error.stack && <details className="mt-2" data-id="element-4634">
                      <summary className="text-xs font-bold text-rose-800 cursor-pointer hover:text-rose-900" data-id="element-4635">
                        Stack Trace
                      </summary>
                      <pre className="text-[10px] font-mono text-rose-600 mt-2 overflow-x-auto whitespace-pre-wrap" data-id="element-4636">
                        {this.state.error.stack}
                      </pre>
                    </details>}
                </div>
              </div>
            </div>}

          <Button onClick={this.handleReset} variant="primary" size="xl" className="shadow-xl shadow-rose-200" data-id="element-4637">
            <RotateCcw className="w-5 h-5 mr-2" data-id="element-4638" />
            Doorgaan met volgende vraag
          </Button>
        </Card>;
    }
    return this.props.children;
  }
}